import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';
import { useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { is404Error } from '~/shared/error-code/error-code';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { IconChevronRightSmall, IconCrossSmall } from '~/ui/core/icons';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { Tile, TileContent, TileControlGroup, tileContentEdgeOffsetClassNames } from '~/ui/core/tile/Tile';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';
import { HeaderContainer, NavigationButtons } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import type { AutomationDetailResponse, AutomationDetailTask, AutomationLayer } from '../../data-contract/automation';
import { useUrlTileEscapeClose } from '../../url-tiles';
import { useAutomationDetail } from '../api/use-automation-detail';
import { WorkHandover } from '../chart/WorkHandover';
import { BandChip } from '../components/BandChip';
import { BandDistributionBar } from '../components/BandDistributionBar';
import { Breadcrumb } from '../components/Breadcrumb';
import { QueryError } from '../components/QueryError';
import { useLocale } from '../components/use-locale';
import {
    bandOfExposure,
    complementOf,
    formatPercent,
    handoverTranslationKey,
    POTENTIAL_BANDS,
    potentialBandIndex,
    quadrantOf,
    quadrantTranslationKey,
} from '../model/automation';
import { itemHandoverFlows } from '../model/item-handover';

const TASK_GRID_COLUMNS = 'minmax(0,1fr) 80px 16px';
const ANCESTOR_LAYERS: readonly AutomationLayer[] = ['family', 'cluster', 'role', 'level'];
type ItemTileLinks = {
    readonly onOpenJsaItem: (input: { readonly itemId: string; readonly layer: 'level' | 'role' }) => void;
    readonly onOpenJsaTask: (taskId: string) => void;
};

const ItemTileHeader: FunctionComponent<{ readonly onClose: () => void }> = ({ onClose }) => {
    const translate = useTranslate();

    return (
        <div className={clsx('bg-surface-raised pb-m pt-tile-edge', tileContentEdgeOffsetClassNames)}>
            <HeaderContainer>
                <NavigationButtons>
                    <TileControlGroup>
                        <Button
                            aria-label={translate('common.close')}
                            iconOnly={true}
                            size="sm"
                            variant="secondary"
                            onClick={onClose}
                        >
                            <IconCrossSmall />
                        </Button>
                    </TileControlGroup>
                </NavigationButtons>
            </HeaderContainer>
        </div>
    );
};

const ItemTileFrame: FunctionComponent<{
    readonly children: ReactNode;
    readonly onClose: () => void;
    readonly testId: string;
}> = ({ children, onClose, testId }) => (
    <Tile data-testid={testId}>
        {/* Radix wraps the viewport content in a `display: table` box that grows to max-content; block it so `min-w-0` and `truncate` can bite. */}
        <ScrollArea
            className="min-w-0 flex-1"
            scrollFade="vertical"
            scrollFadeSize="var(--spacing-4xl)"
            viewportClassName="[&>div]:block!"
        >
            <TileContent className="min-h-full w-full">
                <div className="flex w-full flex-1 flex-col gap-3xl">
                    <ItemTileHeader onClose={onClose} />
                    {children}
                </div>
            </TileContent>
        </ScrollArea>
    </Tile>
);

const OpenLink: FunctionComponent<{
    readonly label: string;
    readonly onClick: () => void;
    readonly testId: string;
}> = ({ label, onClick, testId }) => (
    <Button data-testid={testId} size="sm" type="button" variant="secondary" onClick={onClick}>
        {label}
    </Button>
);

const ItemTitle: FunctionComponent<{
    readonly detail: AutomationDetailResponse;
    readonly layer: AutomationLayer;
    readonly onOpenJsaItem: ItemTileLinks['onOpenJsaItem'];
}> = ({ detail, layer, onOpenJsaItem }) => {
    const translate = useTranslate();
    const ancestors = ANCESTOR_LAYERS.slice(0, ANCESTOR_LAYERS.indexOf(layer)).map(
        (ancestor) => detail.ancestorTitles[ancestor],
    );

    return (
        <div className="flex flex-col gap-xs">
            <Breadcrumb
                className="inline-flex min-w-0 items-center text-xs font-medium leading-normal text-text-content-subtle"
                parts={ancestors}
            />
            <h2 className="m-0 text-2xl font-medium leading-tight tracking-tight text-text-primary [overflow-wrap:anywhere]">
                {detail.item.title}
            </h2>
            {layer === 'role' || layer === 'level' ? (
                <div className="flex flex-wrap items-center gap-xl pt-xs">
                    <OpenLink
                        label={translate(
                            layer === 'role'
                                ? 'pages.ai-transformation.item-tile.open-role'
                                : 'pages.ai-transformation.item-tile.open-level',
                        )}
                        testId="ai-transformation-open-jsa-item"
                        onClick={() => onOpenJsaItem({ itemId: detail.item.id, layer })}
                    />
                </div>
            ) : null}
        </div>
    );
};

/** The item's assessed effort split by the band each task's own score falls in, as shares of the assessed part. */
const bandDistribution = (tasks: readonly AutomationDetailTask[]): readonly number[] => {
    const shareByBand = POTENTIAL_BANDS.map(() => 0);
    let total = 0;

    for (const task of tasks) {
        const band = bandOfExposure(task.taskScore);

        if (task.effortShare === null || task.effortShare === undefined || band === undefined) {
            continue;
        }

        const index = potentialBandIndex(band);
        shareByBand[index] = (shareByBand[index] ?? 0) + task.effortShare;
        total += task.effortShare;
    }

    return total === 0 ? shareByBand : shareByBand.map((share) => share / total);
};

const SummaryRow: FunctionComponent<{
    readonly children: ReactNode;
    readonly divided?: boolean;
    readonly label: string;
}> = ({ children, divided = true, label }) => (
    <div
        className={`flex min-h-10 items-center gap-m px-m py-s ${divided ? 'shadow-[inset_0_1px_0_var(--color-border-soft)]' : ''}`}
    >
        <dt className="min-w-0 flex-1 truncate text-xs text-text-muted">{label}</dt>
        <dd className="m-0 flex min-w-0 flex-wrap items-center justify-end gap-x-m gap-y-xxs text-sm text-text-primary">
            {children}
        </dd>
    </div>
);

const ItemSummary: FunctionComponent<{ readonly detail: AutomationDetailResponse }> = ({ detail }) => {
    const translate = useTranslate();
    const locale = useLocale();
    const { item, method } = detail;
    const distribution = useMemo(() => bandDistribution(detail.tasks), [detail.tasks]);
    const quadrant = typeof item.exposure === 'number' ? quadrantOf(item.exposure, method) : undefined;

    return (
        <section className="flex flex-col gap-xxs overflow-hidden rounded-xl bg-surface-hover p-xxs shadow-sm">
            <dl className="m-0 flex flex-col overflow-hidden rounded-lg bg-surface-raised p-0 shadow-sm">
                <SummaryRow divided={false} label={translate('pages.ai-transformation.kpi.exposure')}>
                    <BandChip band={bandOfExposure(item.exposure)} testId="ai-transformation-item-band" />
                    <span className="tabular-nums" data-testid="ai-transformation-item-exposure">
                        {formatPercent(item.exposure, locale)}
                    </span>
                    <span className="whitespace-nowrap text-xs text-text-muted">
                        {formatPercent(detail.exposureLower, locale)} – {formatPercent(detail.exposureUpper, locale)}
                    </span>
                </SummaryRow>
                <SummaryRow label={translate('pages.ai-transformation.kpi.remaining')}>
                    <span className="tabular-nums">
                        {formatPercent(detail.remaining ?? complementOf(item.exposure), locale)}
                    </span>
                </SummaryRow>
                {quadrant === undefined ? null : (
                    <SummaryRow label={translate('pages.ai-transformation.item-tile.quadrant')}>
                        <span>{translate(quadrantTranslationKey(quadrant, 'title'))}</span>
                    </SummaryRow>
                )}
                <SummaryRow label={translate('pages.ai-transformation.item-tile.task-mix')}>
                    <BandDistributionBar className="min-w-32" distribution={distribution} />
                    <span className="text-xs tabular-nums text-text-secondary">
                        {translate('pages.ai-transformation.item-tile.coverage', {
                            coverage: formatPercent(item.coverage, locale),
                        })}
                    </span>
                </SummaryRow>
            </dl>
        </section>
    );
};

const TaskRow: FunctionComponent<{
    readonly onOpen: () => void;
    readonly task: AutomationDetailTask;
}> = ({ onOpen, task }) => {
    const translate = useTranslate();
    const locale = useLocale();
    const band = bandOfExposure(task.taskScore);
    const tooltip = [
        task.taskScore === null || task.taskScore === undefined
            ? undefined
            : `${translate('pages.ai-transformation.item-tile.task-own-share')}: ${formatPercent(task.taskScore, locale)} (${formatPercent(task.taskScoreLower, locale)} – ${formatPercent(task.taskScoreUpper, locale)})`,
        task.effortShare === null || task.effortShare === undefined
            ? undefined
            : `${translate('pages.ai-transformation.item-tile.task-effort-share')}: ${formatPercent(task.effortShare, locale)}`,
        task.releasedShare === null || task.releasedShare === undefined
            ? undefined
            : `${translate('pages.ai-transformation.item-tile.task-released')}: ${formatPercent(task.releasedShare, locale)}`,
    ].filter((line): line is string => line !== undefined);

    return (
        <li className="list-none" data-testid="ai-transformation-task-row">
            <button
                aria-label={translate('pages.ai-transformation.item-tile.open-task')}
                className="m-0 grid w-full cursor-pointer items-center gap-m border-none bg-transparent px-m py-s text-left font-sans text-xs hover:bg-surface-hover focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-focus-ring [&:not(:first-child)]:shadow-[inset_0_1px_0_var(--color-border-soft)]"
                data-testid="ai-transformation-task-link"
                style={{ gridTemplateColumns: TASK_GRID_COLUMNS }}
                type="button"
                onClick={onOpen}
            >
                <Tooltip delayDuration={120}>
                    <Tooltip.Trigger asChild={true}>
                        <span className="min-w-0 truncate text-text-primary">{task.title}</span>
                    </Tooltip.Trigger>
                    {tooltip.length === 0 ? null : (
                        <Tooltip.Portal>
                            <Tooltip.Content className="max-w-72" side="left">
                                <div className="flex flex-col gap-xxs">
                                    {tooltip.map((line) => (
                                        <span key={line}>{line}</span>
                                    ))}
                                </div>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    )}
                </Tooltip>
                <BandChip band={band} label={translate(handoverTranslationKey(band))} />
                <IconChevronRightSmall className="shrink-0 text-text-content-faint" size={16} />
            </button>
        </li>
    );
};

const TaskList: FunctionComponent<{
    readonly detail: AutomationDetailResponse;
    readonly onOpenJsaTask: (taskId: string) => void;
}> = ({ detail, onOpenJsaTask }) => {
    const translate = useTranslate();
    const { tasks } = detail;
    const flows = useMemo(() => itemHandoverFlows(tasks), [tasks]);
    const sorted = useMemo(
        () => [...tasks].sort((left, right) => (right.releasedShare ?? -1) - (left.releasedShare ?? -1)),
        [tasks],
    );

    return (
        <section className="flex flex-col gap-m" data-testid="ai-transformation-tasks-section">
            <WorkHandover compact={true} data={{ flows, assessedItemCount: flows.length > 0 ? 1 : 0, itemCount: 1 }} />
            <div className="overflow-hidden rounded-xl bg-surface-hover p-xxs shadow-sm">
                <div
                    className="grid gap-m px-m py-xs text-xs font-medium text-text-content-subtle"
                    style={{ gridTemplateColumns: TASK_GRID_COLUMNS }}
                >
                    <span>{translate('pages.ai-transformation.item-tile.tasks')}</span>
                    <span>{translate('pages.ai-transformation.item-tile.task-column-handover')}</span>
                    <span aria-hidden={true} className="w-4" />
                </div>
                <ul
                    className="m-0 flex list-none flex-col overflow-hidden rounded-lg bg-surface-raised p-0 shadow-sm"
                    data-testid="ai-transformation-task-list"
                >
                    {sorted.map((task) => (
                        <TaskRow
                            key={`${task.taskId}:${task.taskVersion}`}
                            task={task}
                            onOpen={() => onOpenJsaTask(task.taskId)}
                        />
                    ))}
                </ul>
            </div>
        </section>
    );
};

const layerOfPath = (detail: AutomationDetailResponse): AutomationLayer => {
    const { id } = detail.item;

    if (detail.path.level === id) {
        return 'level';
    }

    if (detail.path.role === id) {
        return 'role';
    }

    return detail.path.cluster === id ? 'cluster' : 'family';
};

export const ItemTile: FunctionComponent<
    ItemTileLinks & {
        readonly architectureId: string;
        readonly itemId: string;
        readonly onClose: () => void;
    }
> = ({ architectureId, itemId, onClose, onOpenJsaItem, onOpenJsaTask }) => {
    const translate = useTranslate();
    const locale = useLocale();
    useUrlTileEscapeClose(onClose);
    const query = useAutomationDetail({ acceptLanguage: locale, architectureId, itemId });

    if (query.isLoading) {
        return (
            <ItemTileFrame testId="ai-transformation-item-tile-loading" onClose={onClose}>
                <div
                    aria-label={translate('pages.ai-transformation.item-tile.loading')}
                    className="flex flex-col gap-4xl"
                    role="status"
                >
                    <SkeletonBase className="h-8 w-4/5" />
                    <SkeletonBase className="h-40 w-full" />
                    <SkeletonBase className="h-24 w-full" />
                </div>
            </ItemTileFrame>
        );
    }

    if (query.isError || query.data === undefined) {
        return (
            <ItemTileFrame testId="ai-transformation-item-tile-error" onClose={onClose}>
                <div className="w-full flex-1 pt-4xl">
                    <QueryError
                        isRetrying={query.isFetching}
                        message={
                            is404Error(query.error)
                                ? 'pages.ai-transformation.item-tile.not-found'
                                : 'pages.ai-transformation.item-tile.error'
                        }
                        onRetry={() => void query.refetch()}
                    />
                </div>
            </ItemTileFrame>
        );
    }

    const detail = query.data;
    const layer = layerOfPath(detail);

    return (
        <ItemTileFrame testId="ai-transformation-item-tile" onClose={onClose}>
            <ItemTitle detail={detail} layer={layer} onOpenJsaItem={onOpenJsaItem} />
            {detail.item.exposure === null || detail.item.exposure === undefined ? (
                <Badge variant="gray">{translate('pages.ai-transformation.item-tile.not-assessed')}</Badge>
            ) : (
                <ItemSummary detail={detail} />
            )}
            <TaskList detail={detail} onOpenJsaTask={onOpenJsaTask} />
        </ItemTileFrame>
    );
};
